import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The canonical data-table shell (audit M3). A bordered, rounded, horizontally
 * scrollable wrapper; a `muted/40` uppercase header; body rows separated by a
 * per-row top border (never `divide-y`) with a `muted/30` hover. Numeric
 * columns right-align + mono via `className` at the call site
 * (`text-right font-mono tabular-nums`). Empty states render as a Card
 * above/instead of the table — never a `<tr colspan>` row.
 */
function Table({
  className,
  bordered = true,
  ...props
}: React.ComponentProps<'table'> & {
  /** Wrap in a rounded, bordered box. Set false when the table already sits
   *  inside a bordered card (avoids a double border). */
  bordered?: boolean;
}) {
  return (
    <div className={cn('w-full overflow-x-auto', bordered && 'rounded-md border')}>
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead className={cn('bg-muted/40 text-left', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      className={cn(
        '[&_tr]:border-t [&_tr]:border-border [&_tr]:transition-colors [&_tr:hover]:bg-muted/30',
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(props.onClick && 'cursor-pointer', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-brand-slate',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td className={cn('px-3 py-2 text-sm tabular-nums text-brand-navy', className)} {...props} />
  );
}

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
